import React, { useState, useEffect } from 'react';

interface OgImagePreviewProps {
  tokenAddress: string;
}

interface SectionConfig {
  name: string;
  section: string;
}

const SECTIONS: SectionConfig[] = [
  { name: 'Smart Money', section: 'smart-money' },
  { name: 'Admin Mod', section: 'admin-mod' },
];

interface OgImageState {
  imageUrl: string | null;
  loading: boolean;
  error: string | null;
}

const OgImagePreview: React.FC<OgImagePreviewProps> = ({ tokenAddress }) => {
  const [images, setImages] = useState<Record<string, OgImageState>>({});

  useEffect(() => {
    const fetchOgImages = async () => {
      const initialState: Record<string, OgImageState> = {};
      SECTIONS.forEach(({ section }) => {
        initialState[section] = { imageUrl: null, loading: true, error: null };
      });
      setImages(initialState);

      for (const { section } of SECTIONS) {
        const targetUrl = `https://moneyball.money/search?keyword=${tokenAddress}&section=${section}`;
        const microlinkUrl = `https://api.microlink.io/?url=${encodeURIComponent(targetUrl)}`;

        try {
          const response = await fetch(microlinkUrl);
          const data = await response.json();

          if (data.status === 'success' && data.data?.image?.url) {
            setImages((prev) => ({
              ...prev,
              [section]: { imageUrl: data.data.image.url, loading: false, error: null },
            }));
          } else {
            setImages((prev) => ({
              ...prev,
              [section]: { imageUrl: null, loading: false, error: 'No OG image found' },
            }));
          }
        } catch (err) {
          setImages((prev) => ({
            ...prev,
            [section]: { imageUrl: null, loading: false, error: 'Failed to fetch' },
          }));
        }
      }
    };

    if (tokenAddress) {
      fetchOgImages();
    }
  }, [tokenAddress]);

  const getTargetUrl = (section: string) =>
    `https://moneyball.money/search?keyword=${tokenAddress}&section=${section}`;

  return (
    <div className="mb-4 p-4 bg-gray-800 rounded-lg border border-gray-700">
      <div className="flex items-center gap-2 mb-3">
        <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        <span className="text-sm font-medium text-purple-400">OG Image Preview</span>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {SECTIONS.map(({ name, section }) => {
          const state = images[section] || { imageUrl: null, loading: true, error: null };
          const targetUrl = getTargetUrl(section);

          return (
            <div key={section} className="bg-gray-900 rounded-lg overflow-hidden border border-gray-700">
              <div className="px-3 py-2 bg-gray-750 border-b border-gray-700">
                <span className="text-xs font-medium text-gray-300">{name}</span>
              </div>

              <div className="aspect-video relative bg-gray-800">
                {state.loading && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-400"></div>
                  </div>
                )}

                {!state.loading && state.error && (
                  <div className="absolute inset-0 flex items-center justify-center text-gray-500 text-sm">
                    {state.error}
                  </div>
                )}

                {!state.loading && state.imageUrl && (
                  <img
                    src={state.imageUrl}
                    alt={`${name} OG preview`}
                    className="w-full h-full object-cover"
                  />
                )}
              </div>

              <div className="px-3 py-2 border-t border-gray-700">
                <a
                  href={targetUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-purple-400 hover:text-purple-300 hover:underline"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                  Open Link
                </a>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OgImagePreview;
